/* Reusable transaction table — used by Step 2 (Clean) and Step 4 (Classify). */
import React, { useMemo, useState } from "react";
import { ArrowsLeftRight, WarningCircle, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { C } from "./shared";
import { extractErrorMessage } from "@/lib/apiError";

export function TransactionTable({ txns, acct, coa, chart, refresh, mode }) {
  const [filter, setFilter] = useState("all");
  const list = useMemo(() => {
    if (filter === "unclassified") return txns.filter((t) => !t.dropped && !t.internal_transfer && (!t.account_code || !t.confirmed));
    if (filter === "transfers") return txns.filter((t) => t.internal_transfer);
    if (filter === "dropped") return txns.filter((t) => t.dropped);
    return txns;
  }, [txns, filter]);

  const patch = async (tx_id, body) => {
    try { await api.patch(`/mgmt/accounts/${acct.account_id}/transactions/${tx_id}`, body); await refresh(); }
    catch (e) { toast.error(extractErrorMessage(e, "Update failed")); }
  };
  const del = async (tx_id) => {
    if (!window.confirm("Delete this transaction permanently?")) return;
    try { await api.delete(`/mgmt/accounts/${acct.account_id}/transactions/${tx_id}`); await refresh(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="mgmt-transactions-table">
      <div className="flex flex-wrap gap-2 mb-3">
        {[["all", `All (${txns.length})`], ["unclassified", "Unclassified"], ["transfers", "Internal transfers"], ["dropped", "Dropped"]].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
            data-testid={`mgmt-txn-filter-${id}`}
            className="text-[11px] font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors"
            style={{
              backgroundColor: filter === id ? C.teal : "rgba(0,0,0,0.3)",
              borderColor: filter === id ? C.teal : "rgba(255,255,255,0.1)",
              color: filter === id ? "#fff" : "rgba(255,255,255,0.6)",
            }}>{label}</button>
        ))}
      </div>

      <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 min-w-[200px]">Description</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Src</th>
              {mode === "classify" && <th className="px-3 py-2 min-w-[180px]">Account code</th>}
              {mode === "classify" && <th className="px-3 py-2">AI hint</th>}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-8 text-center text-white/30">No transactions match this filter.</td></tr>
            )}
            {list.map((t) => (
              <tr key={t.tx_id} className={`border-t border-white/5 ${t.dropped ? "opacity-40 line-through" : ""} ${t.internal_transfer ? "bg-amber-500/5" : ""}`}
                data-testid={`mgmt-txn-row-${t.tx_id}`}>
                <td className="px-3 py-2 font-mono-num text-white/65 whitespace-nowrap">{t.date || "—"}</td>
                <td className="px-3 py-2 max-w-md truncate" title={t.description}>{t.description || "—"}</td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                    style={{ backgroundColor: t.type === "CREDIT" ? "rgba(10,139,133,0.2)" : "rgba(245,158,11,0.15)", color: t.type === "CREDIT" ? C.tealSoft : "#F59E0B" }}>
                    {t.type}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{Number(t.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 font-mono-num text-white/40">{t.source}</td>
                {mode === "classify" && (
                  <td className="px-3 py-2">
                    <select value={t.account_code || ""} onChange={(e) => patch(t.tx_id, { account_code: e.target.value || null })}
                      data-testid={`mgmt-txn-code-${t.tx_id}`}
                      className="w-full px-2 py-1 rounded text-xs"
                      style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }}>
                      <option value="">— select —</option>
                      <option value="9999">9999 · Suspense (still deciding)</option>
                      {chart && chart.map((a) => (
                        <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                      ))}
                    </select>
                    {t.account_code && !t.confirmed && (
                      <button onClick={() => patch(t.tx_id, { account_code: t.account_code })}
                        data-testid={`mgmt-txn-confirm-${t.tx_id}`}
                        className="mt-1 text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: C.tealSoft }}>Confirm ↓</button>
                    )}
                    {t.confirmed && <div className="text-[10px] mt-1" style={{ color: C.teal }}>✓ confirmed</div>}
                  </td>
                )}
                {mode === "classify" && (
                  <td className="px-3 py-2 max-w-[220px] text-white/55 text-[11px]">
                    {t.ai_suggestion ? (
                      <>
                        <div className="font-mono-num font-bold" style={{ color: t.ai_suggestion.confidence === "high" ? C.tealSoft : C.orange }}>
                          {t.ai_suggestion.code}
                        </div>
                        <div className="truncate" title={t.ai_suggestion.reason}>{t.ai_suggestion.reason}</div>
                      </>
                    ) : "—"}
                  </td>
                )}
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    {!t.dropped && (
                      <button onClick={() => patch(t.tx_id, { internal_transfer: !t.internal_transfer })}
                        title={t.internal_transfer ? "Unflag transfer" : "Flag as internal transfer"}
                        data-testid={`mgmt-txn-transfer-${t.tx_id}`}
                        className="text-white/30 hover:text-amber-400 p-1">
                        <ArrowsLeftRight size={12} />
                      </button>
                    )}
                    <button onClick={() => patch(t.tx_id, { dropped: !t.dropped })}
                      title={t.dropped ? "Restore" : "Drop"}
                      data-testid={`mgmt-txn-drop-${t.tx_id}`}
                      className="text-white/30 hover:text-orange-400 p-1">
                      <WarningCircle size={12} />
                    </button>
                    <button onClick={() => del(t.tx_id)} title="Delete"
                      data-testid={`mgmt-txn-delete-${t.tx_id}`}
                      className="text-white/30 hover:text-red-400 p-1">
                      <Trash size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
